import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCreateBlockNote, useEditorChange } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";

import type { Doc, DocPatch } from "../../shared/types";
import {
  AUDIENCES,
  AUDIENCE_LABEL,
  KINDS,
  KIND_LABEL,
  STATUSES,
  STATUS_LABEL,
} from "../../shared/types";
import { api } from "../api";
import { formatRelative } from "../format";
import { ArrowLeftIcon } from "../components/icons";
import { parseBlocks } from "./DocPage";

type SaveState = "saved" | "unsaved" | "saving" | "error";

const AUTOSAVE_MS = 1200;

function Editor({ doc }: { doc: Doc }) {
  const navigate = useNavigate();

  const editor = useCreateBlockNote({ initialContent: parseBlocks(doc.content_json) }, [doc.id]);

  const [title, setTitle] = useState(doc.title);
  const [summary, setSummary] = useState(doc.summary);
  const [kind, setKind] = useState(doc.kind);
  const [status, setStatus] = useState(doc.status);
  const [audience, setAudience] = useState(doc.audience);
  const [team, setTeam] = useState(doc.team ?? "");

  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [savedAt, setSavedAt] = useState(doc.updated_at);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const slugRef = useRef(doc.slug);
  const timerRef = useRef<number | null>(null);
  // Held in a ref so the debounce timer always reads the latest values without
  // being torn down and rebuilt on every keystroke.
  const pendingRef = useRef({ title, summary, kind, status, audience, team });
  pendingRef.current = { title, summary, kind, status, audience, team };

  const save = useCallback(async () => {
    setSaveState("saving");
    setErrorMessage(null);

    const current = pendingRef.current;
    const patch: DocPatch = {
      title: current.title.trim() || "Untitled document",
      summary: current.summary,
      kind: current.kind,
      status: current.status,
      audience: current.audience,
      team: current.team.trim() || null,
      content_json: JSON.stringify(editor.document),
      content_md: editor.blocksToMarkdownLossy(editor.document),
    };

    try {
      const { document } = await api.update(doc.id, patch);
      setSaveState("saved");
      setSavedAt(document.updated_at);
      // A draft's slug follows its title, so keep the address bar honest.
      if (document.slug !== slugRef.current) {
        slugRef.current = document.slug;
        navigate(`/d/${document.slug}/edit`, { replace: true });
      }
    } catch (err) {
      setSaveState("error");
      setErrorMessage((err as Error).message);
    }
  }, [doc.id, editor, navigate]);

  const scheduleSave = useCallback(() => {
    setSaveState("unsaved");
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void save(), AUTOSAVE_MS);
  }, [save]);

  useEditorChange(scheduleSave, editor);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  // Ctrl/Cmd-S saves now rather than letting the browser offer the page.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (timerRef.current) window.clearTimeout(timerRef.current);
        void save();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [save]);

  // Losing unsaved work to a stray tab close is the one failure that matters.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (saveState === "unsaved" || saveState === "saving") event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saveState]);

  const remove = useCallback(async () => {
    if (!window.confirm(`Delete “${doc.title}”? It disappears from the handbook.`)) return;
    try {
      await api.remove(doc.id);
      navigate("/", { replace: true });
    } catch (err) {
      setErrorMessage((err as Error).message);
    }
  }, [doc.id, doc.title, navigate]);

  const stateLabel =
    saveState === "saving"
      ? "Saving…"
      : saveState === "unsaved"
        ? "Unsaved changes"
        : saveState === "error"
          ? (errorMessage ?? "Save failed")
          : `Saved ${formatRelative(savedAt)}`;

  return (
    <div className="shell">
      <div className="editor-bar">
        <Link className="backlink" to={`/d/${slugRef.current}`} style={{ margin: 0 }}>
          <ArrowLeftIcon /> Done
        </Link>

        <div className="editor-fields">
          <select
            className="field-select"
            value={kind}
            aria-label="Document kind"
            onChange={(event) => {
              setKind(event.target.value as typeof kind);
              scheduleSave();
            }}
          >
            {KINDS.map((option) => (
              <option key={option} value={option}>
                {KIND_LABEL[option]}
              </option>
            ))}
          </select>

          <select
            className="field-select"
            value={status}
            aria-label="Status"
            onChange={(event) => {
              setStatus(event.target.value as typeof status);
              scheduleSave();
            }}
          >
            {STATUSES.map((option) => (
              <option key={option} value={option}>
                {STATUS_LABEL[option]}
              </option>
            ))}
          </select>

          <select
            className="field-select"
            value={audience}
            aria-label="Audience"
            onChange={(event) => {
              setAudience(event.target.value as typeof audience);
              scheduleSave();
            }}
          >
            {AUDIENCES.map((option) => (
              <option key={option} value={option}>
                {AUDIENCE_LABEL[option]}
              </option>
            ))}
          </select>

          <input
            className="field-select"
            style={{ width: 130 }}
            value={team}
            placeholder="Team (optional)"
            aria-label="Team"
            onChange={(event) => {
              setTeam(event.target.value);
              scheduleSave();
            }}
          />

          <span className="savestate" data-state={saveState}>
            {stateLabel}
          </span>

          <button className="btn btn-quiet btn-danger" onClick={remove}>
            Delete
          </button>
        </div>
      </div>

      <div className="doc-body">
        <input
          className="title-input"
          value={title}
          placeholder="Untitled document"
          aria-label="Title"
          onChange={(event) => {
            setTitle(event.target.value);
            scheduleSave();
          }}
        />
        <input
          className="summary-input"
          value={summary}
          placeholder="One line that says what this document decides or describes"
          aria-label="Summary"
          onChange={(event) => {
            setSummary(event.target.value);
            scheduleSave();
          }}
        />

        <BlockNoteView editor={editor} theme="dark" />
      </div>
    </div>
  );
}

export default function EditPage() {
  const { slug = "" } = useParams();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadedId = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    // Reloading on every slug change would remount the editor after an
    // autosave renamed the draft, so only the first load for a document wins.
    api
      .get(slug)
      .then((result) => {
        if (!live || loadedId.current === result.document.id) return;
        loadedId.current = result.document.id;
        setDoc(result.document);
      })
      .catch((err: Error) => live && setError(err.message));
    return () => {
      live = false;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="shell">
        <Link className="backlink" to="/">
          <ArrowLeftIcon /> Handbook
        </Link>
        <div className="notice">
          {error === "not_found" ? "That document does not exist." : error}
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="shell">
        <div className="notice">Loading…</div>
      </div>
    );
  }

  return <Editor doc={doc} />;
}
